import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import PatientUtils from 'services/PatientUtils';
import { PatientImage, PhysicianImage } from 'components/Common/UserImage';
import GenericBadge from 'components/Utils/GenericBadge';
import SelectableListItem from 'components/Utils/SelectableListItem';
import MiscUtils from 'services/MiscUtils';
import CalendarUtils from 'services/CalendarUtils';
import Icon from 'components/Utils/NewIcon';
import RouteUtils from 'services/RouteUtils';
import DefaultChip from 'components/Utils/DefaultChip';
import ProgressMessage from 'components/Utils/ProgressMessage';
import StaffUtils from 'services/StaffUtils';
import './style.scss';
import Badge from '@material-ui/core/Badge';
import AppointmentUtils from 'services/AppointmentUtils';
import DefaultTooltip from 'components/Utils/DefaultTooltip';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import DefaultModal from 'components/Utils/DefaultModal';
import Dymo from 'services/Dymo';
import PhysicianUtils from 'services/PhysicianUtils';
import moment from 'moment-timezone';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { useNavStack } from 'careand-redux/slices/ui/hooks';
import { Stacks } from 'careand-redux/slices/ui/navigation';
import { GetStaff, IsTypePhlebotomist } from 'careand-redux/selectors';
import ClinicUtils from 'services/ClinicUtils';
import PerformanceUtils from 'services/PerformanceUtils';
import { useMediaQuery } from '@material-ui/core';
import QueueBadgeMobileActionButtons from 'scenes/HomeStaff/ChatQueue/QueueBadgeMobileActionButtons';
const BADGE_STYLE = { width: '100%' };

export const STATUS = {
  waiting: 'waiting',
  called_in: 'called_in',
  help: 'help',
  clean: 'clean',
  travelling: 'travelling',
};

const DEFAULT_ARRAY = [];

const QueueBadge = ({ element, subscriber, actionButtonsNounce, hideActionButtons }) => {
  const [showActionButtons, setShowActionButtons] = useState(false);
  const dispatch = useAppDispatch();
  const nav = useNavStack(Stacks.Master);
  const isPhone = useMediaQuery('(max-width: 767px)');

  const [showInlineDescription, setShowInlineDescription] = useState(false);
  const [showMobileActionButtons, setShowMobileActionButtons] = useState(false);

  const [loading, setLoading] = useState(false);

  const patientId = element.patient_id;

  const patient = useAppSelector((state) => state.patients.byId[patientId]);
  const appointments =
    useAppSelector((state) => state.appointments.byId[patientId]) || DEFAULT_ARRAY;
  const bubbles = useAppSelector((state) => state.ui.bubbles);
  let staff = useAppSelector((state) =>
    GetStaff(state, element.physician_id || patient?.primary_physician, false),
  );
  const { role, user } = useAppSelector((state) => state.authentication);
  const ownSubscription = useAppSelector((state) => state.payments.subscriptions[patientId]);

  const appointment = useMemo(() => {
    return appointments.find((appointment) => appointment.id === element.appointment_id);
  }, [appointments, element.appointment_id]);

  useEffect(() => {
    if (!ownSubscription) {
      dispatch(api.Payments.getUserSubscription({ userId: patientId }));
    }
  }, [dispatch, ownSubscription, patientId]);

  useEffect(() => {
    if (element.appointment_id && !appointment) {
      dispatch(
        api.Appointments.getAppointment({
          appointmentId: element.appointment_id,
        }),
      );
    }
  }, [element, appointment, dispatch]);

  useEffect(() => {
    if (!patient && element) {
      dispatch(api.Patients.getPatient({ id: patientId }));
    }
  }, [patient, element, patientId, dispatch]);

  const update = useCallback(
    async (fields) => {
      let copy = {
        ...PerformanceUtils.clone(element),
        wait_until: 0,
        wait_message: '',
        ...fields,
      };
      return modifyQueue(async () =>
        dispatch(
          api.Queue.updateQueueElement({
            id: copy.id,
            queueElement: copy,
          }),
          { errorTitle: 'Queue Patient' },
        ),
      );
    },
    [dispatch, element],
  );

  const remove = useCallback(async () => {
    return modifyQueue(async () =>
      dispatch(api.Queue.deleteQueueElement({ id: element.id }), {
        errorTitle: 'Queue Patient',
      }),
    );
  }, [dispatch, element]);

  async function modifyQueue(action) {
    setLoading(true);
    await action();
    setLoading(false);
  }

  function goToCPP() {
    let route;

    const bubble = bubbles.find(
      ({ appointment_id }) => appointment_id && appointment_id === element.appointment_id,
    );

    if (bubble) {
      route = RouteUtils.getRoute(RouteUtils.routes().bubble, {
        ':bubbleId': bubble.id,
      });
    } else if (patient) {
      route = RouteUtils.getRoute(RouteUtils.routes().patient, {
        ':patientId': patient.id,
      });
    }

    if (route) nav.push(route);
  }

  let className = `queue-badge full-width animated fadeIn ${element.status} ${
    loading ? 'loading' : ''
  } ${subscriber ? 'subscriber' : ''}`;
  if (element.wait_message || element.wait_until) className += ' wait-until';

  let staffName = staff ? StaffUtils.getName(staff) : 'None';

  function getIconSrc() {
    if (patient) return AppointmentUtils.getIcon(appointment);
    if (element.status === STATUS.clean) return 'broom';
  }

  const iconAlt =
    AppointmentUtils.getAppointmentDescription(appointment) +
    ' ' +
    (subscriber ? '(Subscriber)' : '(Pay-per-Use)');
  const iconSrc = getIconSrc();

  const badgeTitle = patient ? PatientUtils.getName(patient) || '' : staffName;
  const badgeAvatar = patient ? (
    <PatientImage user={patient} size={40} />
  ) : (
    <PhysicianImage user={staff} size={40} />
  );

  useEffect(() => {
    setShowActionButtons(false);
    setShowInlineDescription(false);
  }, [actionButtonsNounce]);

  return (
    <div
      className={className}
      onClick={() => {
        hideActionButtons();
        setShowMobileActionButtons(true);
      }}
    >
      <div className="type-icon">
        {iconSrc && (
          <Icon
            src={iconSrc}
            alt={iconAlt}
            width="15px"
            height="15px"
            fontAwesomeStyle="fas"
            showTooltip={true}
            className={patient && (subscriber ? 'white' : 'blue')}
          />
        )}
      </div>
      <ProgressOverlay loading={loading} />
      <div className="content">
        {(showActionButtons || !isPhone) && (
          <Buttons
            element={element}
            role={role}
            remove={remove}
            update={update}
            appointment={appointment}
            goToCPP={goToCPP}
            user={user}
            patient={patient}
          />
        )}
        {isPhone && showMobileActionButtons && (
          <QueueBadgeMobileActionButtons
            onClickShowDescription={() => {
              if (!showInlineDescription) {
                setShowMobileActionButtons(false);
              }
              setShowInlineDescription((prev) => !prev);
            }}
            onClickShowActions={() => {
              setShowActionButtons(true);
              setShowMobileActionButtons(false);
            }}
          />
        )}
        <div className="list-item-wrapper">
          <SelectableListItem
            key={element.id}
            selected={false}
            padded={false}
            className="list-item"
            item={
              <GenericBadge
                id={patient ? `queue-badge-${patient.id}` : undefined}
                title={badgeTitle}
                style={BADGE_STYLE}
                avatar={badgeAvatar}
                info={
                  patient ? (
                    <span className="info">
                      <span>
                        {CalendarUtils.format(element.date, 'h:mm A')}{' '}
                        {PatientUtils.getOhipNumber(patient)}
                      </span>
                      <br />
                      <span>{staffName}</span>
                    </span>
                  ) : (
                    <span className="info">
                      <span>{CalendarUtils.format(element.date, 'h:mm A')} </span>
                      <br />
                      <span className="capitalize">{element.status} Requested</span>
                    </span>
                  )
                }
              />
            }
          />
        </div>
        <QueueChip element={element} remove={remove} appointment={appointment} update={update} />
      </div>
      {showInlineDescription && (
        <div className="expanded-content pl-8 text-sm">
          {appointment && AppointmentUtils.getConcernComplaintsText(appointment)}
        </div>
      )}
    </div>
  );
};

const ProgressOverlay = React.memo(
  ({ loading }) =>
    !!loading && (
      <div className="loading-overlay">
        <ProgressMessage show={loading} size={50} />
      </div>
    ),
);

const Buttons = ({ element, remove, update, role, appointment, goToCPP, user, patient }) => (
  <div className="button-wrapper">
    {role === 'physician' && (
      <PhysicianButtons
        element={element}
        remove={remove}
        update={update}
        appointment={appointment}
        goToCPP={goToCPP}
        user={user}
        patient={patient}
      />
    )}
    {role === 'admin' && (
      <AdminButtons
        element={element}
        remove={remove}
        update={update}
        appointment={appointment}
        goToCPP={goToCPP}
        patient={patient}
      />
    )}
  </div>
);

const PhysicianButtons = ({ element, remove, update, appointment, goToCPP, user, patient }) => {
  const clinic = useAppSelector((state) => state.resource.clinic);
  const physicians = useAppSelector((state) => state.physicians.byId);
  const physician = physicians[user.id] || user;

  const { status, room } = element;
  const { default_room } = physician;

  const isHomeAppointment = AppointmentUtils.isHomeAppointment(appointment);
  const isClinicAppointment = !appointment || AppointmentUtils.isClinicAppointment(appointment);

  const isWaiting = status === STATUS.waiting;
  const isTravelling = status === STATUS.travelling;
  const isPhlebotomist = useAppSelector(IsTypePhlebotomist);

  const rooms = useMemo(() => {
    let rooms = clinic.rooms || [];

    let result = rooms.filter((roomNumber) =>
      status === STATUS.called_in ? roomNumber !== room : true,
    );
    if (default_room) {
      result = rooms.filter((roomNumber) => roomNumber === default_room);
    }
    return result;
  }, [default_room, status, room, clinic]);

  const onRoomClick = (room) => {
    update({
      room,
      status: STATUS.called_in,
      wait_until: undefined,
      wait_message: undefined,
    });
  };

  return (
    <>
      {isHomeAppointment && (
        <>
          {isWaiting && <StartTravelButton update={update} />}
          {(isWaiting || isTravelling) && <StartAppointmentButton update={update} room="home" />}
        </>
      )}
      {isClinicAppointment && !!patient && (
        <>
          {isWaiting &&
            rooms.map((room) => <RoomButton key={room} onClick={onRoomClick} room={room} />)}
          <SelectRoomButton onClick={onRoomClick} />
        </>
      )}
      {!!appointment && isClinicAppointment && isPhlebotomist && (
        <QuestionnaireButton appointment={appointment} />
      )}
      {!!patient && <ProfileButton goToCPP={goToCPP} />}
      <DeleteButton remove={remove} />
    </>
  );
};

const SelectRoomButton = ({ onClick }) => {
  const [roomModal, setRoomModal] = useState(false);
  return (
    <>
      <RoundButton
        src="door-open"
        className="blue"
        onClick={() => setRoomModal(true)}
        alt="Call in to a different room"
      />
      <SelectRoomModal open={roomModal} setOpen={setRoomModal} onClick={onClick} />
    </>
  );
};

export const SelectRoomModal = ({
  open,
  setOpen,
  onClick,
  message = 'Select a room to call',
  defaultRoom,
}) => {
  const rooms = useAppSelector((state) => state.resource.clinic?.rooms);

  return (
    open && (
      <DefaultModal
        className="room-modal"
        title={message}
        content={
          <div className="row">
            <div className="buttons">
              {(rooms || []).map((currentRoom) => (
                <RoomButton
                  key={currentRoom}
                  room={currentRoom}
                  onClick={(room) => {
                    setOpen(false);
                    onClick(room);
                  }}
                  blue={currentRoom === defaultRoom}
                />
              ))}
            </div>
          </div>
        }
        isOpen={open}
        onRequestClose={() => setOpen(false)}
        wrap={true}
        padding={true}
      />
    )
  );
};

const RoomButton = ({ onClick, room, blue = false }) => {
  const queue = useAppSelector((state) => state.queue.elements);
  const physicians = useAppSelector((state) => state.physicians.byId);
  const authentication = useAppSelector((state) => state.authentication);
  const busyRooms = useMemo(() => ClinicUtils.getBusyRooms(queue), [queue]);
  const [showConfirmation, setShowConfirmation] = useState();

  function onClickWrapper() {
    let message;

    if (busyRooms.includes(room)) {
      let element = queue.find((element) => element.room === room);
      let physicianString = '';

      if (!!element && !!element.physician_id && authentication.user.id !== element.physician_id) {
        let physician = physicians[element.physician_id];
        if (physician) physicianString = ` by ${PhysicianUtils.getName(physician)}`;
        message = `${room} is currently in use${physicianString}. Are you sure you want to call patient in?`;
      }
    }

    if (!!message) {
      setShowConfirmation(message);
    } else {
      onClick(room);
    }
  }

  const STYLE = { marginLeft: '2px' };
  let className = 'icon-room';
  if (blue) className += ` blue`;

  let roomSmall = useMemo(() => {
    return room
      .split(' ')
      .map((word) => word[0])
      .join('');
  }, [room]);

  return (
    <>
      <RoundButton
        text={roomSmall}
        className={className}
        onClick={onClickWrapper}
        alt={room}
        style={STYLE}
      />
      {!!showConfirmation && (
        <ConfirmationDialog
          title="Room in use"
          open={!!showConfirmation}
          message={showConfirmation}
          onConfirm={() => onClick(room)}
          onCancel={() => setShowConfirmation()}
        />
      )}
    </>
  );
};

const AdminButtons = ({ element, remove, update, appointment, goToCPP, patient }) => {
  let isHomeAppointment = AppointmentUtils.isHomeAppointment(appointment);
  let isClinicAppointment = AppointmentUtils.isClinicAppointment(appointment);
  let isWaiting = element.status === STATUS.waiting;

  return (
    <>
      {!!patient && (
        <>
          {isHomeAppointment && isWaiting && <StartTravelButton update={update} />}
          {!!appointment && isClinicAppointment && (
            <QuestionnaireButton appointment={appointment} />
          )}
          <ProfileButton goToCPP={goToCPP} />
          <PrintLabelButton appointment={appointment} />
        </>
      )}
      <DeleteButton remove={remove} />
    </>
  );
};

const QuestionnaireButton = ({ appointment }) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState(false);

  let src = ['fas', 'table-list'];
  let questionnaireExists = !!appointment.questionnaire;

  async function onSendQuestionnaire(force = false) {
    if (!force && questionnaireExists) {
      return setConfirmationDialog(true);
    }
    setConfirmationDialog(false);
    return sendBySMS();
  }

  async function sendBySMS() {
    setLoading('sms');
    await dispatch(api.Resource.sendQuestionnaireBySMS({ id: appointment.id }), {
      errorTitle: 'Send Questionnaire by SMS',
    });
    setLoading(false);
  }

  return (
    <>
      <RoundButton
        src={src}
        height="25px"
        alt="Send Questionnaire"
        onClick={() => onSendQuestionnaire(false)}
        loading={loading}
      />
      {confirmationDialog && (
        <ConfirmationDialog
          title="Confirmation Needed"
          message="Patient already responded to questionnaire. Do you still want to send it?"
          open={confirmationDialog}
          onConfirm={() => onSendQuestionnaire(true)}
          onCancel={() => setConfirmationDialog(false)}
        />
      )}
    </>
  );
};

export const PrintLabelButton = ({ appointment = {}, className }) => {
  const patient = useAppSelector((state) => state.patients.byId[appointment.patient_id]);

  let extraInfo = undefined;

  if (appointment.concern_id === 'covid_test') extraInfo = 'YM-B KIT';

  function onClick() {
    if (patient) {
      MiscUtils.showToastrInfo({
        title: 'Patient Label',
        message: `Printing 1 label, please wait...`,
      });
      Dymo.printPatientLabel(patient, extraInfo);
    }
  }

  return (
    !!patient &&
    (appointment.type === AppointmentUtils.getAppointmentTypes().clinic ||
      appointment.type === AppointmentUtils.getAppointmentTypes().home) && (
      <RoundButton
        className={className}
        src={['fas', 'print']}
        alt="Print Label"
        onClick={onClick}
      />
    )
  );
};

const ProfileButton = React.memo(({ goToCPP }) => (
  <RoundButton src="user" alt="Go to Profile" onClick={goToCPP} />
));

const DeleteButton = ({ remove }) => (
  <RoundButton src="trash" className="red" onClick={remove} alt="Remove from queue" />
);

const StartAppointmentButton = ({ update, room }) => {
  function onClick() {
    update({ status: STATUS.called_in, room });
  }

  return (
    <RoundButton className="blue" src="calendar-clock" alt="Start Appointment" onClick={onClick} />
  );
};

const StartTravelButton = ({ update }) => {
  function onClick() {
    update({ status: STATUS.travelling });
  }

  return (
    <RoundButton
      className="blue"
      src="location-dot"
      alt="Start Travel"
      style={{ maxWidth: '22px' }}
      onClick={onClick}
    />
  );
};

const QueueChip = ({ element, update, remove, appointment }) => {
  function onClickHelp() {
    update({ status: STATUS.called_in });
  }

  function onClickClean() {
    if (element.physician_id === element.patient_id) {
      remove();
    } else {
      update({ status: STATUS.called_in });
    }
  }

  let isVideo = AppointmentUtils.isTelemedicineAppointment(appointment) || element.room === 'video';
  let isHome = AppointmentUtils.isHomeAppointment(appointment) || element.room === 'home';
  let isClinic = AppointmentUtils.isClinicAppointment(appointment);

  let questionnaire;

  if (appointment && appointment.questionnaire) questionnaire = appointment.questionnaire;

  return (
    <div className="chip-wrapper">
      {element.status === STATUS.travelling && (
        <DefaultChip text="Travelling" backgroundColor="#42bcdd" textColor="white" />
      )}
      {element.status === STATUS.called_in && (
        <>
          {(isVideo || isHome) && (
            <DefaultChip text="Started" backgroundColor="#42bcdd" textColor="white" />
          )}
          {!!element.room && !isVideo && !isHome && (
            <DefaultChip text={element.room} backgroundColor="#42bcdd" textColor="white" />
          )}
        </>
      )}
      {element.status === STATUS.waiting && (
        <DefaultChip text="Waiting" backgroundColor="#42bcdd" textColor="white" />
      )}
      {element.status === STATUS.help && (
        <DefaultChip
          element={
            <span>
              Help
              <Icon className="white" src={['fas', 'xmark']} width="15px" height="15px" />
            </span>
          }
          backgroundColor="#ff3b5e"
          textColor="white"
          onClick={onClickHelp}
        />
      )}
      {element.status === STATUS.clean && (
        <DefaultChip
          element={
            <span>
              Clean
              <Icon className="white" src={['fas', 'xmark']} width="15px" height="15px" />
            </span>
          }
          backgroundColor="#FFAE03"
          textColor="white"
          onClick={onClickClean}
        />
      )}
      <div className="row icon-wrapper-wrapper">
        {!!appointment && (
          <QuestionnaireBadge questionnaire={questionnaire} appointment={appointment} />
        )}
        {!!appointment && <StudentConsentBadge questionnaire={questionnaire} element={element} />}
        {!!appointment && <ConcernBadge element={appointment || element} />}
        {(element.wait_until || element.wait_message) && <TimeRemaningBadge element={element} />}
      </div>
    </div>
  );
};

export const StudentConsentBadge = ({
  questionnaire,
  element,
  showTooltip = true,
  onDescription,
}) => {
  const studentQuestion = questionnaire?.questions?.find(({ question }) =>
    question.toLowerCase().includes('student'),
  );

  const studentConsent = studentQuestion
    ? (studentQuestion.answer || '').toLowerCase() === 'yes'
    : undefined;

  let declined, tooltipText;

  if (studentConsent === true) {
    declined = false;
    tooltipText = 'Student consent granted';
  } else if (studentConsent === false) {
    declined = true;
    tooltipText = 'Student consent declined';
  } else if (element?.student_consent) {
    declined = false;
    tooltipText = 'Student consent granted by default (no patient answer)';
  } else {
    return null;
  }

  useEffect(() => {
    if (onDescription) onDescription(tooltipText);
  }, [tooltipText]);

  return (
    <div className={`icon-wrapper student-consent${declined ? ' declined' : ''}`}>
      <Icon
        src="graduation-cap"
        className="white"
        alt={tooltipText}
        showTooltip={showTooltip}
        height="20px"
        tooltipPlace="left"
      />
    </div>
  );
};

export const QuestionnaireBadge = ({ appointment = {}, questionnaire = {} }) => {
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);

  const isClinicAppointment = AppointmentUtils.isClinicAppointment(appointment);
  if (!isClinicAppointment) return null;

  let questionnaireExists = !!questionnaire.questions;

  let tooltipText = 'Questionnaire not filled by patient!';
  let icon = ['fas', 'table-list'];
  let className = 'white';

  if (questionnaireExists) {
    let isOk = AppointmentUtils.isQuestionnaireOk(questionnaire);
    tooltipText = isOk ? 'PPE Not Required' : 'Full PPE Required';
    icon = isOk ? 'head-side' : 'head-side-mask';
    className = isOk ? 'white' : 'red';

    let questions = (questionnaire || {}).questions || [];

    let noAnswers = questions.filter((question) => question.answer === 'No');
    let yesAnswers = questions.filter((question) => question.answer === 'Yes');

    tooltipText += `\n\nNegative answers: ${noAnswers.length}`;
    tooltipText += `\nPositive answers: ${yesAnswers.length}`;
    tooltipText += `\nClick to see all answers`;
  }

  return (
    <>
      {showQuestionnaire && (
        <DefaultModal
          className="modal questionnaire-modal"
          title="Questionnaire"
          wrap={true}
          padding={true}
          content={
            <div className="full-questionnaire">
              {(questionnaire.questions || []).map((question, index) => (
                <div className="question column" style={{ marginTop: '10px' }} key={index}>
                  <pre>
                    <FormattedText text={question.question} />
                  </pre>
                  <span className="bold">{question.answer}</span>
                </div>
              ))}
            </div>
          }
          isOpen={showQuestionnaire}
          onRequestClose={() => setShowQuestionnaire(false)}
        />
      )}
      <div className="icon-wrapper">
        <Icon
          src={icon}
          className={className + ' ppe-badge' + (questionnaireExists ? ' clickable' : '')}
          alt={tooltipText}
          showTooltip={true}
          height="20px"
          tooltipPlace="left"
          onClick={questionnaireExists ? () => setShowQuestionnaire(true) : undefined}
        />
      </div>
    </>
  );
};

function FormattedText({ text }) {
  // Decode HTML entities
  const decodeHTML = (html) => {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  };

  return <div dangerouslySetInnerHTML={{ __html: decodeHTML(text) }} />;
}

const TimeRemaningBadge = ({ element }) => {
  const TEXT_TRUNCATE_LEN = 10;
  const [text, setText] = useState();

  useEffect(() => {
    function getText() {
      let text = (element.wait_message || '').substring(0, TEXT_TRUNCATE_LEN);
      if (text.length === TEXT_TRUNCATE_LEN) text += '...';
      if (element.wait_until) {
        let now = moment();
        let waitUntil = moment.unix(element.wait_until);
        if (now.isBefore(waitUntil)) {
          let diff = waitUntil.diff(now);
          text = moment.utc(diff).format('mm:ss');
        } else {
          text = '00:00';
        }
      }
      setText(text);
    }

    let interval = setInterval(getText, 1000);
    getText();
    return () => {
      clearInterval(interval);
    };
  }, [element]);

  return (
    <DefaultChip
      text={text}
      alt={element.wait_message}
      backgroundColor="var(--color-status-success)"
      textColor="white"
      className="truncate"
    />
  );
};

const ConcernBadge = ({ element }) => {
  let tooltipText = AppointmentUtils.getConcernComplaintsText(element);

  return (
    !!element.concern_minutes && (
      <NumberBadge
        id={'queue-' + element.id}
        number={element.concern_minutes}
        popupContent={tooltipText}
      />
    )
  );
};

const numberStyles = makeStyles({
  root: {
    margin: 'auto',
  },
  badge: {
    backgroundColor: 'var(--color-bg-surface)',
  },
});

export const NumberBadge = ({ id, number, popupContent, tooltipClassName = undefined }) => {
  const classes = numberStyles();
  return (
    <DefaultTooltip
      placement="left"
      title={popupContent}
      classes={tooltipClassName ? { tooltip: tooltipClassName } : undefined}
    >
      <div className="icon-wrapper">
        <Badge classes={classes} data-tip data-for={id} badgeContent={number} showZero={true} />
      </div>
    </DefaultTooltip>
  );
};

/**
 * @param {any} props
 */
export const RoundButton = ({
  src,
  text,
  className = 'blue',
  onClick,
  style,
  alt,
  tooltipPlace = 'top',
  height = '25px',
  loading = false,
}) => (
  <div
    className={'round-button box-shadow grow-hover clickable ' + className}
    onClick={loading ? null : onClick}
  >
    {!!src && (
      <Icon
        fontAwesomeStyle="fad"
        src={src}
        loading={loading}
        className={className}
        height={height}
        style={style}
        alt={alt}
        showTooltip={!!alt}
        tooltipPlace={tooltipPlace}
      />
    )}
    {!!text && (
      <DefaultTooltip placement={tooltipPlace} showTooltip={!!alt} title={alt}>
        <span>{text}</span>
      </DefaultTooltip>
    )}
  </div>
);
QueueBadge.propTypes = {
  element: PropTypes.object.isRequired,
  number: PropTypes.number,
  subscriber: PropTypes.bool,
};

export default QueueBadge;
